import { ReactNode, useEffect, useState } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { apiUrl, getToken, clearAuth } from '@/utils/api'

interface ProtectedRouteProps {
  children: ReactNode
}

const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const location = useLocation()
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null)

  useEffect(() => {
    let cancelled = false

    const checkAuth = async () => {
      const token = getToken()
      if (!token) {
        if (!cancelled) setIsAuthenticated(false)
        return
      }

      // Validate the token against the backend instead of trusting localStorage.
      try {
        const res = await fetch(apiUrl('/api/auth/me'), {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (cancelled) return
        if (res.ok) {
          setIsAuthenticated(true)
        } else {
          clearAuth()
          setIsAuthenticated(false)
        }
      } catch (error) {
        if (cancelled) return
        console.error('Error validating session:', error)
        setIsAuthenticated(false)
      }
    }

    checkAuth()
    return () => {
      cancelled = true
    }
  }, [])

  // Show loading state while checking authentication
  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-800 mx-auto mb-4"></div>
          <p className="text-gray-600">Checking authentication...</p>
        </div>
      </div>
    )
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/admin/login" state={{ from: location }} replace />
  }

  // Render protected content
  return <>{children}</>
}

export default ProtectedRoute
